import React, { FC, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CurrencyContext } from 'context/currency';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { CONSTANTS } from 'config/constants';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { actionPriceListForOrderTemplate, materialPriceListForOrderTemplate } from 'apollo/templates/client-directory';
import ActionsDataGrid from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/actions-data-grid/actions-data-grid.component';
import { OrderLabAction } from 'models/order-input/lab-order';
import { ActionGridInput, MaterialGridInput } from 'models/order-input/shared';
import { MaterialPriceList } from 'models/material';
import { Order, Restoration } from 'models/order';
import { getCategorizedActionsTemplate } from 'apollo/templates/order';
import {
    convertCategorizedActionsToOrderLabActions,
    toMaterialMacroOptions,
} from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/actions-and-materials-helper';
import { ActionSelectionPayload } from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/actions-data-grid/action-selection';
import PostSaveMaterialPrompts from './post-save-material-prompts.component';
import { Alert, Box, Button } from '@mui/material';
import { isRealCurrency, shouldShowCurrencyMissingAlert } from 'utils/currency';
import { getGridCurrencyMismatch } from '../grid-currency-mismatch';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { addRestorationMaterialsTemplate, updateRestorationTemplate } from 'apollo/templates/order';
import {
    gridToRestorationMaterialInputs,
    isTioTandvardRestoration,
    isWarrantyRestoration,
    labRowDefaultQuantity,
    perToothQuantityOf,
    restorationActionsToGridInputs,
    restorationMaterialsToGridInputs,
    restorationWithGridRows,
} from 'components/order-flow/shared/restoration-input-helper';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { useParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

type ActionsDataGridWrapperProps = {
    /** The restoration whose action rows the grid edits. */
    restoration: Restoration;
    onEditComplete?: () => void;
    onMaterialsPersisted?: () => void;
};

/**
 * The actions grid on the order details page, for every order shape (DD-2506 step 3):
 * saving sends the restoration back through `updateRestoration`, rows sent with their id
 * keep it and the server prices the rest. Materials the picked actions call for are asked
 * about after the save and added with `addRestorationMaterials`.
 */
const ActionsDataGridWrapper: FC<ActionsDataGridWrapperProps> = ({
    restoration,
    onEditComplete,
    onMaterialsPersisted,
}: ActionsDataGridWrapperProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'orders']);
    const { currency, setCurrency } = useContext(CurrencyContext);
    // For an action-based order the page currency comes from the clinic's lab connection; when
    // that resolves nothing there is no currency to save the rows in.
    const hasRealCurrency = isRealCurrency(currency);
    const { showBoundary } = useDenthubErrorBoundary();
    const { orderId } = useParams<{ orderId: string }>();
    const { order } = useContext(OrderDetailsContext);
    const customerId = order.customer?._id as string;
    const clinicOrderVersion = order.clinicOrderVersion;
    const warranty = isWarrantyRestoration(restoration) || Boolean(order.isWarrantyOrder);
    const isTioTandvard = isTioTandvardRestoration(restoration);
    const [actionPriceList, setActionPriceList] = useState<OrderLabAction[]>([]);
    const [actionStates, setActionStates] = useState<ActionGridInput[]>([]);
    const [isActionGridValid, setIsActionGridValid] = useState<boolean>(true);
    // Currency the loaded action price data is denominated in; null once loaded with none.
    const [priceListCurrency, setPriceListCurrency] = useState<string | null>();
    const [materialsAddedSnackbarOpen, setMaterialsAddedSnackbarOpen] = useState(false);
    // Actions picked during this editing session that have macros/actionMaterials to
    // resolve. Keyed by actionId - row indices shift as the grid drops empty rows.
    const pendingSelectionsRef = useRef<Map<string, ActionSelectionPayload>>(new Map());
    const [promptQueue, setPromptQueue] = useState<ActionSelectionPayload[]>([]);
    // The restoration as saved, so materials chosen afterwards can name their action row.
    const savedRestorationRef = useRef<Restoration>(restoration);
    const currencyMismatch = getGridCurrencyMismatch('action', order.currency, currency, priceListCurrency);

    const { data: { materialPriceListForClient } = {} } = useQuery(materialPriceListForOrderTemplate, {
        variables: { clientId: customerId },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError(error) {
            showBoundary(error);
        },
    });

    const actionPriceListWithValidMacros = useMemo(() => {
        if (!materialPriceListForClient) {
            return actionPriceList;
        }
        return (actionPriceList || []).map((action) => ({
            ...action,
            materialMacroOptions: toMaterialMacroOptions(
                action.materialMacros,
                (materialPriceListForClient as MaterialPriceList).materials,
            ),
        }));
    }, [actionPriceList, materialPriceListForClient]);

    useEffect(() => {
        setActionStates(restorationActionsToGridInputs(restoration.actions));
        savedRestorationRef.current = restoration;
        return () => {
            setActionStates([]);
        };
        // Reset on another order or restoration only; a refetched copy of the same one keeps the edits.
    }, [order?._id, restoration._id]);

    useEffect(() => {
        if (clinicOrderVersion === CONSTANTS.SETTINGS_VALUES.SIMPLE) {
            getActionPriceListForClient({ variables: { clientId: customerId } });
        } else {
            getCategorizedActions({ variables: { clinicId: customerId, orderId } });
        }
    }, [orderId, clinicOrderVersion]);

    const [getActionPriceListForClient] = useLazyQuery(actionPriceListForOrderTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onCompleted({ actionPriceListForClient }) {
            setPriceListCurrency(actionPriceListForClient?.currency ?? null);
            if (!order.currency || order.currency === 'XXX') {
                setCurrency(actionPriceListForClient?.currency);
            } else {
                setCurrency(order.currency);
            }
            setActionPriceList(actionPriceListForClient.actions);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getCategorizedActions] = useLazyQuery(getCategorizedActionsTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted: ({ categorizedActions }) => {
            // Every row carries the same currency: the one set on the clinic's connection to
            // this lab, falling back to the action set's default.
            setPriceListCurrency(categorizedActions?.[0]?.currency ?? null);
            if (order.currency && order.currency !== 'XXX') {
                setCurrency(order.currency);
            } else if (categorizedActions?.[0]?.currency) {
                setCurrency(categorizedActions[0].currency);
            }
            setActionPriceList(convertCategorizedActionsToOrderLabActions(categorizedActions));
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const [updateRestoration, { loading: saving }] = useMutation(updateRestorationTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [addRestorationMaterials] = useMutation(addRestorationMaterialsTemplate, {
        onCompleted: () => {
            setMaterialsAddedSnackbarOpen(true);
            if (onMaterialsPersisted) {
                onMaterialsPersisted();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const disableForActionBased = clinicOrderVersion !== CONSTANTS.SETTINGS_VALUES.SIMPLE;

    const handleChange = (actions: ActionGridInput[], isGridValid: boolean) => {
        // The grid reports on every row effect; an unchanged set must not restart the cycle.
        setActionStates((previous) => (isEqual(previous, actions) ? previous : actions));
        setIsActionGridValid(isGridValid);
    };

    const handleSaveClick = async () => {
        const result = await updateRestoration({
            variables: {
                orderId: order._id,
                restoration: restorationWithGridRows(
                    restoration,
                    actionStates,
                    restorationMaterialsToGridInputs(restoration.materials, restoration.actions),
                ),
            },
        });
        // A failed save must not prompt for materials - onError already surfaced it.
        const savedOrder = result?.data?.updateRestoration as Order | undefined;
        if (!savedOrder) {
            return;
        }
        savedRestorationRef.current =
            savedOrder.restorations?.find((saved) => saved._id === restoration._id) ?? restoration;
        // The actions are persisted now, so it is safe to ask about their materials.
        // Only actions still on the grid at save time are prompted for; rows the user
        // removed or re-pointed beforehand are dropped. Quantity is re-read from the
        // live row rather than the value captured at selection, since the user can edit
        // it before saving - and the macro's materials inherit it.
        const gridRowsByActionId = new Map(actionStates.map((actionRow) => [actionRow.actionId, actionRow]));
        const reconciledQueue = Array.from(pendingSelectionsRef.current.values())
            .filter((selection) => gridRowsByActionId.has(selection.actionId))
            .map((selection) => ({
                ...selection,
                quantity: gridRowsByActionId.get(selection.actionId)?.quantity || selection.quantity,
            }));
        pendingSelectionsRef.current = new Map();
        if (reconciledQueue.length > 0) {
            setPromptQueue(reconciledQueue);
            return;
        }
        if (onEditComplete) {
            onEditComplete();
        }
    };

    const handleCancelClick = () => {
        // Nothing was persisted for these selections, so drop them with the edit.
        pendingSelectionsRef.current = new Map();
        if (onEditComplete) {
            onEditComplete();
        }
    };

    const handleActionSelected = (selection: ActionSelectionPayload) => {
        pendingSelectionsRef.current.set(selection.actionId, selection);
    };

    // Added once, after the whole prompt queue has been walked. The materials name the
    // saved action row they were chosen for.
    const handlePromptsResolved = (newMaterialInputs: MaterialGridInput[]) => {
        setPromptQueue([]);
        if (newMaterialInputs.length === 0) {
            if (onEditComplete) {
                onEditComplete();
            }
            return;
        }
        const savedActionRows = restorationActionsToGridInputs(savedRestorationRef.current.actions);
        addRestorationMaterials({
            variables: {
                orderId: order._id,
                restorationId: restoration._id,
                materials: gridToRestorationMaterialInputs(newMaterialInputs, savedActionRows),
            },
        }).finally(() => {
            if (onEditComplete) {
                onEditComplete();
            }
        });
    };

    return (
        <Fragment>
            {shouldShowCurrencyMissingAlert(currency, priceListCurrency) && (
                <Box mb={1}>
                    <Alert severity="error" data-test="actions-data-grid-wrapper.currency-alert">
                        {translate('orders:ACTION_CURRENCY_MISSING')}
                    </Alert>
                </Box>
            )}
            {currencyMismatch && (
                <Box mb={1}>
                    <Alert severity="error" data-test="actions-data-grid-wrapper.currency-mismatch-alert">
                        {translate(`orders:${currencyMismatch.errorKey}`, currencyMismatch.values)}
                    </Alert>
                </Box>
            )}
            <ActionsDataGrid
                actions={actionStates}
                priceList={actionPriceListWithValidMacros}
                onChange={handleChange}
                onActionSelected={handleActionSelected}
                deferMaterialDialogs
                warranty={warranty}
                isTioTandvard={isTioTandvard}
                isActionBasedOrder={clinicOrderVersion === CONSTANTS.SETTINGS_VALUES.ACTION_BASED}
                disableForActionBased={disableForActionBased}
                defaultQuantity={labRowDefaultQuantity(
                    restoration,
                    clinicOrderVersion === CONSTANTS.SETTINGS_VALUES.ACTION_BASED,
                )}
                perToothQuantity={perToothQuantityOf(restoration)}
            />
            <Box mt={1}>
                <LoadingButton
                    size="small"
                    loading={saving}
                    disabled={!isActionGridValid || !hasRealCurrency || !!currencyMismatch}
                    onClick={() => handleSaveClick()}
                    variant="contained"
                    sx={{ marginRight: 2 }}
                    data-test="actions-data-grid-wrapper.save-btn"
                >
                    {translate('common:SAVE')}
                </LoadingButton>
                <Button
                    size="small"
                    onClick={() => handleCancelClick()}
                    data-test="actions-data-grid-wrapper.cancel-btn"
                >
                    {translate('common:CANCEL')}
                </Button>
            </Box>
            {promptQueue.length > 0 ? (
                <PostSaveMaterialPrompts
                    queue={promptQueue}
                    priceListMaterials={(materialPriceListForClient as MaterialPriceList)?.materials || []}
                    onResolved={handlePromptsResolved}
                />
            ) : undefined}
            {materialsAddedSnackbarOpen ? (
                <AlertSnackbar type="success" message={translate('actions-materials:MATERIALS_ADDED_TO_PRODUCT')} />
            ) : undefined}
        </Fragment>
    );
};

export default ActionsDataGridWrapper;
